import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Send,
  Mic,
  Square,
  Timer,
  AlertOctagon,
  Loader2,
  GraduationCap,
  User,
  HelpCircle,
  CheckCircle2,
  Eye,
  Brain,
  Sparkles,
  ShieldAlert,
} from 'lucide-react';
import {
  ALL_DIMS,
  DIM_LABEL,
  HELP_LABEL,
  HELP_PENALTY,
  type ChatTurn,
  type Dim,
  type HelpType,
  type Modality,
  type TurnResponse,
} from '../types';
import { effectiveBudgetCapSec } from '@marzano/contracts';
import { Alert } from './ui';
import { LearnerInterviewView } from './learner/LearnerInterviewView';

export interface GrantedHelpItem {
  help_type: HelpType;
  penalty_pct: number;
  at: string;
  dim: Dim | null;
}

interface InterviewPanelProps {
  sessionId: string;
  siteId: string;
  initialBudgetSec: number;
  oralSecondsUsedInitial: number;
  textSecondsUsedInitial: number;
  modality: Modality;
  studentName: string;
  oaCodigo: string;
  hitoNombre: string;
  chatHistory: ChatTurn[];
  currentDim: Dim | null;
  evidenceCounts: Record<Dim, number>;
  grantedHelps: GrantedHelpItem[];
  isAwaitingFirst: boolean;
  isBusy: boolean;
  variant?: 'teacher' | 'learner';
  topicLabel?: string;
  // undefined = double-click bloqueado por CT-H5 lock ref.
  onSubmitText: (
    answer: string,
    secondsUsed: number,
    metadata?: { paste_attempts: number; answer_time_ms: number },
  ) => Promise<TurnResponse | undefined>;
  onSubmitAudio: (audio: Blob, durationSec: number) => Promise<TurnResponse | undefined>;
  onGrantHelp: (helpType: HelpType) => Promise<void>;
  onCloseEarly: () => Promise<void>;
  /** Disparado por el cronómetro al llegar a 0. Idempotente (guard de useRef). */
  onTimeExhausted: () => Promise<void>;
}

export function InterviewPanel(props: InterviewPanelProps) {
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  // Anti-doble-click: el cierre es idempotente en el backend pero cada request
  // gasta LLM (dictamen final). Lock local hasta que resuelva.
  const [closing, setClosing] = useState(false);
  const closeLockRef = useRef(false);

  const handleCloseEarly = async (): Promise<void> => {
    if (closeLockRef.current) return;
    closeLockRef.current = true;
    setClosing(true);
    try {
      await props.onCloseEarly();
    } finally {
      // Ruta normal: el caller navega a la vista de dictamen y este componente
      // se desmonta antes de restaurar el flag. Si algo falla y el usuario
      // sigue en pantalla, dejamos que reintente después del error.
      closeLockRef.current = false;
      setClosing(false);
    }
  };
  const startTypingRef = useRef<number | null>(null);
  // Anti-fraude: paste attempts + tiempo desde que se mostró la pregunta van al aiDetector como prior.
  const pasteAttemptsRef = useRef(0);
  const questionShownAtRef = useRef<number>(Date.now());
  const [pasteBlockedFlash, setPasteBlockedFlash] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStartRef = useRef<number>(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Cronómetro con pausa durante processing: el tiempo LLM (5-30s) no le come budget al alumno.
  // Mismo tope que usa el servidor para el cierre duro: el inicial de la sesión
  // (fijado por el tipo de actividad) acotado por el mix oral/escrito en mixta.
  const effectiveBudget = effectiveBudgetCapSec(
    props.initialBudgetSec,
    props.modality,
    props.oralSecondsUsedInitial,
    props.textSecondsUsedInitial,
  );
  const initialRemaining =
    effectiveBudget - props.oralSecondsUsedInitial - props.textSecondsUsedInitial;
  const [remainingSec, setRemainingSec] = useState(initialRemaining);
  const tickStartRef = useRef<number>(Date.now());
  const pausedTotalRef = useRef(0);
  const pauseStartRef = useRef<number | null>(null);

  const isProcessing = props.isBusy || submitting;
  const timeExpired = remainingSec <= 0;
  useEffect(() => {
    if (isProcessing) {
      if (pauseStartRef.current === null) pauseStartRef.current = Date.now();
    } else if (pauseStartRef.current !== null) {
      pausedTotalRef.current += Date.now() - pauseStartRef.current;
      pauseStartRef.current = null;
    }
  }, [isProcessing]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const now = Date.now();
      // Restar la pausa en curso además del pausedTotal ya committeado.
      const ongoingPause = pauseStartRef.current !== null ? now - pauseStartRef.current : 0;
      const elapsedSec = Math.floor(
        (now - tickStartRef.current - pausedTotalRef.current - ongoingPause) / 1000,
      );
      setRemainingSec(Math.max(0, initialRemaining - elapsedSec));
    }, 1000);
    return () => window.clearInterval(id);
  }, [initialRemaining]);

  // Auto-cierre idempotente al llegar el timer a 0; si hay turno en vuelo espera.
  const timeExhaustedFiredRef = useRef(false);
  useEffect(() => {
    if (remainingSec > 0) return;
    if (isProcessing) return;
    if (timeExhaustedFiredRef.current) return;
    timeExhaustedFiredRef.current = true;
    void props.onTimeExhausted();
  }, [remainingSec, isProcessing, props]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [props.chatHistory.length]);

  const allowAudio = props.modality !== 'written';
  const allowText = props.modality !== 'oral' && props.modality !== 'simplified_1_3';
  const lastCoach = useMemo(
    () => [...props.chatHistory].reverse().find((t) => t.role === 'coach'),
    [props.chatHistory],
  );

  // Reset anti-fraude en pregunta nueva. Depende del texto — kind/helpType del mismo turno no cuentan.
  useEffect(() => {
    if (lastCoach) {
      questionShownAtRef.current = Date.now();
      pasteAttemptsRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCoach?.text]);

  const handleTypingStart = () => {
    if (startTypingRef.current === null) startTypingRef.current = Date.now();
  };

  const blockClipboardEvent = (
    e: React.ClipboardEvent<HTMLTextAreaElement> | React.SyntheticEvent<HTMLTextAreaElement>,
  ) => {
    e.preventDefault();
    pasteAttemptsRef.current += 1;
    setPasteBlockedFlash(true);
    window.setTimeout(() => setPasteBlockedFlash(false), 1500);
  };

  const submitText = async () => {
    const text = answerText.trim();
    if (!text || submitting) return;
    const secondsUsed = startTypingRef.current
      ? Math.max(1, Math.round((Date.now() - startTypingRef.current) / 1000))
      : 1;
    const answerTimeMs = Math.max(0, Date.now() - questionShownAtRef.current);
    setSubmitting(true);
    setError(null);
    try {
      await props.onSubmitText(text, secondsUsed, {
        paste_attempts: pasteAttemptsRef.current,
        answer_time_ms: answerTimeMs,
      });
      setAnswerText('');
      startTypingRef.current = null;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al enviar respuesta');
    } finally {
      setSubmitting(false);
    }
  };

  const startRecording = async () => {
    setRecordingError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickAudioMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      audioChunksRef.current = [];
      rec.ondataavailable = (ev) => {
        if (ev.data.size > 0) audioChunksRef.current.push(ev.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current = rec;
      recordingStartRef.current = Date.now();
      rec.start(250);
      setRecording(true);
    } catch (e) {
      setRecordingError(
        e instanceof Error ? e.message : 'No se pudo acceder al micrófono.',
      );
    }
  };

  const stopRecordingAndSubmit = () => {
    const rec = mediaRecorderRef.current;
    if (!rec) return;
    rec.onstop = async () => {
      const tracks = (rec.stream as MediaStream).getTracks();
      tracks.forEach((t) => t.stop());
      const blob = new Blob(audioChunksRef.current, {
        type: rec.mimeType || 'audio/webm',
      });
      const elapsedMs = Date.now() - recordingStartRef.current;
      const durationSec = Math.max(1, Math.round(elapsedMs / 1000));
      setRecording(false);

      // Grabaciones muy cortas suelen ser fallo de mic o click prematuro; reintento en front evita 400.
      if (elapsedMs < 1200 || blob.size < 3000) {
        setError(
          'La grabación es muy corta. Pulsa "Grabar" otra vez y habla durante al menos 2 segundos.',
        );
        return;
      }

      setSubmitting(true);
      setError(null);
      try {
        await props.onSubmitAudio(blob, durationSec);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Error al transcribir audio');
      } finally {
        setSubmitting(false);
      }
    };
    rec.stop();
  };

  const [grantingType, setGrantingType] = useState<HelpType | null>(null);
  const [recentGrant, setRecentGrant] = useState<{ type: HelpType; penalty: number } | null>(
    null,
  );
  useEffect(() => {
    if (!recentGrant) return;
    const t = window.setTimeout(() => setRecentGrant(null), 2500);
    return () => window.clearTimeout(t);
  }, [recentGrant]);

  const grantHelp = async (helpType: HelpType) => {
    if (props.isBusy || submitting || grantingType !== null) return;
    setGrantingType(helpType);
    setError(null);
    try {
      await props.onGrantHelp(helpType);
      setRecentGrant({ type: helpType, penalty: HELP_PENALTY[helpType] });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al registrar ayuda');
    } finally {
      setGrantingType(null);
    }
  };

  const helpCounts = props.grantedHelps.reduce(
    (acc, h) => {
      acc[h.help_type] = (acc[h.help_type] ?? 0) + 1;
      return acc;
    },
    {} as Partial<Record<HelpType, number>>,
  );
  const totalPenalty = props.grantedHelps.reduce((s, h) => s + h.penalty_pct, 0);

  if (props.variant === 'learner') {
    return (
      <LearnerInterviewView
        topicLabel={props.topicLabel ?? props.hitoNombre}
        chatHistory={props.chatHistory}
        isAwaitingFirst={props.isAwaitingFirst}
        hasQuestion={!!lastCoach}
        currentDim={props.currentDim}
        remainingSec={remainingSec}
        paused={isProcessing}
        timeExpired={timeExpired}
        closing={closing}
        allowText={allowText}
        allowAudio={allowAudio}
        answerText={answerText}
        onAnswerChange={setAnswerText}
        textGuards={{
          onFocus: handleTypingStart,
          onPaste: blockClipboardEvent,
          onCopy: blockClipboardEvent,
          onCut: blockClipboardEvent,
          onContextMenu: (e) => e.preventDefault(),
          onDrop: (e) => e.preventDefault(),
        }}
        pasteBlockedFlash={pasteBlockedFlash}
        onSubmitText={() => void submitText()}
        recording={recording}
        onStartRecording={() => void startRecording()}
        onStopRecording={stopRecordingAndSubmit}
        recordingError={recordingError}
        error={error}
        grantingType={grantingType}
        helpJustGranted={!!recentGrant}
        onGrantHelp={(h) => void grantHelp(h)}
        onCloseEarly={() => void handleCloseEarly()}
      />
    );
  }

  return (
    <div className="w-full">
      <header className="card mb-4 flex flex-col md:flex-row md:flex-wrap md:items-center gap-3 md:justify-between">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500">
            Entrevista activa
          </div>
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mt-0.5 truncate">
            {props.studentName}
          </h2>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {props.oaCodigo} · {props.hitoNombre}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-4">
          <DimProgress current={props.currentDim} counts={props.evidenceCounts} />
          <Cronometro remainingSec={remainingSec} paused={isProcessing} />
          <button
            className="btn-danger text-xs sm:text-sm py-1.5 sm:py-2"
            onClick={() => void handleCloseEarly()}
            disabled={props.isBusy || submitting || closing}
          >
            {closing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <AlertOctagon className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">
              {closing ? 'Cerrando…' : 'Cierre anticipado'}
            </span>
            <span className="sm:hidden">{closing ? 'Cerrando…' : 'Cerrar'}</span>
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card flex flex-col h-[60vh] sm:h-[600px]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 pr-2">
            {props.isAwaitingFirst && (
              <div className="flex items-center gap-3 text-slate-500 text-sm py-8 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />
                Esperando primera pregunta del coach…
              </div>
            )}
            {props.chatHistory.map((turn, i) => (
              <ChatBubble key={i} turn={turn} />
            ))}
            {(submitting || props.isBusy) && !props.isAwaitingFirst && (
              <TurnProcessingProgress />
            )}
          </div>

          {lastCoach && (
            <div className="mt-3 border-t border-slate-200 pt-3 space-y-2">
              {timeExpired && (
                <Alert kind="error">
                  <strong>Tiempo agotado.</strong> Cerrando entrevista y generando dictamen…
                </Alert>
              )}
              {allowText && (
                <>
                  <textarea
                    className={
                      'field-textarea ' +
                      (pasteBlockedFlash ? 'ring-2 ring-amber-400 transition-shadow' : '')
                    }
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    onFocus={handleTypingStart}
                    onPaste={blockClipboardEvent}
                    onCopy={blockClipboardEvent}
                    onCut={blockClipboardEvent}
                    onContextMenu={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="Escribe la respuesta del estudiante aquí…"
                    rows={2}
                    disabled={submitting || props.isBusy || timeExpired}
                  />
                  {pasteBlockedFlash && (
                    <div className="text-[11px] text-amber-700 flex items-center gap-1 mt-0.5">
                      <AlertOctagon className="w-3 h-3" />
                      Copiar y pegar está deshabilitado durante la evaluación.
                    </div>
                  )}
                </>
              )}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {allowAudio && !recording && (
                    <button
                      type="button"
                      onClick={startRecording}
                      disabled={submitting || props.isBusy || timeExpired}
                      className="btn-secondary"
                    >
                      <Mic className="w-4 h-4 text-marzano-600" />
                      Grabar respuesta oral
                    </button>
                  )}
                  {allowAudio && recording && (
                    <button
                      type="button"
                      onClick={stopRecordingAndSubmit}
                      className="btn-danger"
                    >
                      <Square className="w-4 h-4" />
                      Detener y transcribir
                    </button>
                  )}
                  {recordingError && (
                    <span className="text-xs text-red-600">{recordingError}</span>
                  )}
                </div>
                {allowText && (
                  <button
                    type="button"
                    onClick={submitText}
                    disabled={
                      submitting || props.isBusy || timeExpired || answerText.trim().length === 0
                    }
                    className="btn-primary"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Enviar
                  </button>
                )}
              </div>
              {error && <Alert kind="error">{error}</Alert>}
            </div>
          )}
        </div>

        <aside className="card">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-amber-500" />
              <h3 className="font-semibold text-slate-900">Intervenciones / Ayudas</h3>
            </div>
            {props.grantedHelps.length > 0 && (
              <span
                className={
                  'text-xs font-mono px-2 py-0.5 rounded ' +
                  (totalPenalty <= -20
                    ? 'bg-red-100 text-red-700'
                    : totalPenalty < 0
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-slate-100 text-slate-600')
                }
                title="Penalización acumulada"
              >
                {totalPenalty === 0 ? '0%' : `${totalPenalty}%`}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Otorgar ayuda penaliza la dimensión activa. La penalización aparece en el
            dictamen al cierre.
          </p>

          {recentGrant && (
            <div className="mb-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                <strong>{HELP_LABEL[recentGrant.type]}</strong> registrada
                {recentGrant.penalty !== 0
                  ? ` · penalización ${recentGrant.penalty}%`
                  : ''}
              </span>
            </div>
          )}

          <div className="space-y-2">
            {(Object.keys(HELP_LABEL) as HelpType[]).map((h) => {
              const pct = HELP_PENALTY[h];
              const count = helpCounts[h] ?? 0;
              const isGranting = grantingType === h;
              return (
                <button
                  key={h}
                  type="button"
                  onClick={() => grantHelp(h)}
                  disabled={props.isBusy || submitting || grantingType !== null}
                  className="w-full text-left rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-800 flex items-center gap-2">
                      {isGranting && <Loader2 className="w-3 h-3 animate-spin text-marzano-600" />}
                      {HELP_LABEL[h]}
                      {count > 0 && (
                        <span className="rounded-full bg-marzano-100 text-marzano-700 px-2 py-0.5 text-[10px] font-mono font-semibold">
                          ×{count}
                        </span>
                      )}
                    </span>
                    <span
                      className={
                        'text-xs font-mono ' +
                        (pct === 0
                          ? 'text-emerald-600'
                          : pct >= -10
                            ? 'text-amber-600'
                            : 'text-red-600')
                      }
                    >
                      {pct === 0 ? 'sin penalización' : `${pct}%`}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {props.grantedHelps.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
              {props.grantedHelps.length} ayuda{props.grantedHelps.length === 1 ? '' : 's'} otorgada
              {props.grantedHelps.length === 1 ? '' : 's'} en esta sesión.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function ChatBubble({ turn }: { turn: ChatTurn }) {
  const isCoach = turn.role === 'coach';
  const isHelp = turn.kind === 'help';
  const helpLabel: string | null = turn.helpType
    ? (HELP_LABEL as Record<HelpType, string>)[turn.helpType]
    : null;
  return (
    <div className={'flex gap-2 ' + (isCoach ? '' : 'flex-row-reverse')}>
      <div
        className={
          'flex-shrink-0 rounded-full w-8 h-8 flex items-center justify-center ' +
          (isHelp
            ? 'bg-amber-100 text-amber-700'
            : isCoach
              ? 'bg-marzano-100 text-marzano-700'
              : 'bg-slate-200 text-slate-700')
        }
      >
        {isHelp ? (
          <HelpCircle className="w-4 h-4" />
        ) : isCoach ? (
          <GraduationCap className="w-4 h-4" />
        ) : (
          <User className="w-4 h-4" />
        )}
      </div>
      <div
        className={
          'rounded-2xl px-4 py-2 max-w-[80%] text-sm ' +
          (isHelp
            ? 'bg-amber-50 border border-amber-200 text-slate-800'
            : isCoach
              ? 'bg-white border border-slate-200 text-slate-800'
              : 'bg-marzano-600 text-white')
        }
      >
        {isHelp && helpLabel && (
          <div className="text-[10px] uppercase tracking-wider text-amber-700 mb-0.5">
            Ayuda · {helpLabel}
          </div>
        )}
        {turn.modality && !isCoach && (
          <div className="text-[10px] uppercase tracking-wider text-marzano-100 mb-0.5">
            {turn.modality === 'oral' ? 'voz transcrita' : 'texto'}
          </div>
        )}
        <div className="whitespace-pre-wrap">{turn.text}</div>
        {turn.aiSuspicion && turn.aiSuspicion.level !== 'low' && (
          <AiSuspicionBadge signal={turn.aiSuspicion} />
        )}
      </div>
    </div>
  );
}

// Sospecha ≥medium del aiDetector: informa al docente, no acusa al alumno.
function AiSuspicionBadge({ signal }: { signal: NonNullable<ChatTurn['aiSuspicion']> }) {
  const isHigh = signal.level === 'high';
  return (
    <div
      className={
        'mt-2 rounded-lg px-2 py-1.5 border text-[11px] ' +
        (isHigh
          ? 'bg-red-50 border-red-200 text-red-800'
          : 'bg-amber-50 border-amber-200 text-amber-800')
      }
      role="note"
    >
      <div className="flex items-center gap-1.5 font-semibold">
        <ShieldAlert className="w-3.5 h-3.5" />
        Posible respuesta generada por IA · sospecha {signal.level}
        <span className="ml-auto opacity-70 font-normal">
          {Math.round(signal.confidence * 100)}% conf.
        </span>
      </div>
      <ul className="mt-1 space-y-0.5 list-disc pl-4 opacity-90 font-normal">
        {signal.reasons.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>
    </div>
  );
}

function DimProgress({
  current,
  counts,
}: {
  current: Dim | null;
  counts: Record<Dim, number>;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-xs uppercase tracking-wider text-slate-500 mr-2">
        Nivel actual
      </div>
      <div className="flex gap-1.5">
        {ALL_DIMS.map((d) => {
          const isCurrent = d === current;
          const count = counts[d];
          return (
            <div
              key={d}
              className={
                'rounded-md px-2.5 py-1 text-[11px] font-medium border ' +
                (isCurrent
                  ? 'bg-marzano-600 text-white border-marzano-600'
                  : count > 0
                    ? 'bg-marzano-50 text-marzano-800 border-marzano-200'
                    : 'bg-slate-50 text-slate-500 border-slate-200')
              }
              title={`${DIM_LABEL[d]} — ${count} evidencia(s)`}
            >
              {DIM_LABEL[d].slice(0, 3).toUpperCase()} · {count}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Cronometro({ remainingSec, paused }: { remainingSec: number; paused: boolean }) {
  const mm = Math.floor(remainingSec / 60).toString().padStart(2, '0');
  const ss = Math.floor(remainingSec % 60).toString().padStart(2, '0');
  const warn = remainingSec <= 60;
  return (
    <div
      className={
        'flex items-center gap-2 px-3 py-1.5 rounded-lg border font-mono text-sm transition-colors ' +
        (paused
          ? 'border-slate-300 bg-slate-100 text-slate-600'
          : warn
            ? 'border-red-300 bg-red-50 text-red-700'
            : 'border-amber-300 bg-amber-50 text-amber-700')
      }
      title={paused ? 'Pausado mientras el sistema procesa' : 'Tiempo restante'}
    >
      <Timer className={'w-4 h-4 ' + (paused ? 'animate-pulse' : '')} />
      {mm}:{ss}
      {paused && <span className="ml-1 text-[10px] uppercase tracking-wider">pausado</span>}
    </div>
  );
}

function pickAudioMime(): string | undefined {
  const cands = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  for (const m of cands) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m;
  }
  return undefined;
}

function TurnProcessingProgress() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const startedAt = Date.now();
    const id = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    return () => window.clearInterval(id);
  }, []);

  const stages = [
    {
      key: 'reading',
      icon: Eye,
      title: 'Leyendo tu respuesta',
      subtitle: 'Estoy revisando lo que escribiste con calma…',
    },
    {
      key: 'thinking',
      icon: Brain,
      title: 'Pensando lo aprendido',
      subtitle: 'Estoy reflexionando sobre lo que sabes…',
    },
    {
      key: 'preparing',
      icon: Sparkles,
      title: 'Preparando la próxima pregunta',
      subtitle: 'Eligiendo la mejor pregunta para ti…',
    },
  ];
  // Thresholds calibradas al pipeline real (Assessment 0-4s, Closing+Interview 4-9s, cierre 9s+).
  const currentIdx = elapsed < 4 ? 0 : elapsed < 9 ? 1 : 2;

  // Rotación cada 3s en fase 3 para no sentir el UI congelado.
  const preparingMessages = [
    'Eligiendo la mejor pregunta para ti…',
    'Adaptándola a lo que ya sabes…',
    'Puliendo los detalles…',
    'Un momento más…',
  ];
  const preparingIdx =
    currentIdx === 2
      ? Math.min(
          Math.floor((elapsed - 9) / 3),
          preparingMessages.length - 1,
        )
      : 0;
  const activeSubtitle =
    currentIdx === 2 ? preparingMessages[preparingIdx]! : stages[currentIdx]!.subtitle;

  const Current = stages[currentIdx]!.icon;

  return (
    <div className="my-6 mx-auto max-w-md">
      <div className="rounded-2xl border-2 border-marzano-200 bg-gradient-to-br from-marzano-50 to-white px-6 py-8">
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-marzano-200 animate-ping opacity-40" />
            <div className="relative bg-marzano-100 rounded-full p-5">
              <Current className="w-10 h-10 text-marzano-700" />
            </div>
          </div>
          <div className="mt-5 text-lg font-semibold text-slate-900">
            {stages[currentIdx]!.title}
          </div>
          <div className="mt-1 text-sm text-slate-600 transition-opacity">
            {activeSubtitle}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          {stages.map((s, i) => {
            const done = i < currentIdx;
            const active = i === currentIdx;
            return (
              <div key={s.key} className="flex flex-col items-center gap-1 flex-1">
                <div
                  className={
                    'rounded-full w-3 h-3 transition-all ' +
                    (done
                      ? 'bg-marzano-500'
                      : active
                        ? 'bg-marzano-500 ring-4 ring-marzano-200 scale-125'
                        : 'bg-slate-200')
                  }
                />
                <div
                  className={
                    'text-[10px] uppercase tracking-wider ' +
                    (done || active ? 'text-marzano-700 font-medium' : 'text-slate-400')
                  }
                >
                  {s.key === 'reading' ? 'Lee' : s.key === 'thinking' ? 'Piensa' : 'Prepara'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
